import CachedIcon from '@mui/icons-material/Cached'
import { Box, useTheme } from '@mui/material'
import type { UnknownAction } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaTrashAlt } from 'react-icons/fa'
import { LuRefreshCw } from 'react-icons/lu'
import ReactLoader from 'react-loader'
import { useDispatch } from 'react-redux'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon'
import { useAppSelector } from '@/store'
import { apiService, useRecipeImagesUpdateMutation } from '@/store/apiService'
import {
  type CanvasImage,
  PLACEMENT,
  setDualCanvasImage,
} from '@/store/appSlice'
import {
  clearFLWashBakeState,
  clearStainingCorrectionState,
  clearStumpShadeStainingState,
} from '@/store/fullLayering'
import {
  clearFMStainingState,
  clearFullmonolithicStumpShadeStainingState,
} from '@/store/fullMonoLithic'
import {
  clearMLStainingState,
  clearMicroLayeringStumpShadeStainingState,
} from '@/store/microLayering'
import {
  clearOpaquerStainingState,
  clearPfmCrownStainingCorrectionState,
} from '@/store/pfmCrown'
import {
  clearStainingStudioCrownState,
  clearStumpShadeState,
  clearWashBakeState,
} from '@/store/stainingStudio'
import {
  getDehazedImageUrl,
  isDehazeIconVisible,
  useRecipeCheck,
} from '@/utils/helper'

import type { PresignedUrlResponse } from '../Gallery'

interface GalleryImageProps {
  image: PresignedUrlResponse
  index: number
  isLoading?: boolean
  handleImageDeletion: () => void
  handleImageSelection: () => void
  selectedIcon: React.ReactNode
  isDialogOpen: boolean
  setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  isSelected: boolean
  isDualCanvas: boolean
  isIdentical: boolean
  setNewImages: (images: any) => void
  onRecalibrate: () => void
  position: PLACEMENT
}

const GalleryImage: React.FC<GalleryImageProps> = ({
  image,
  index,
  isLoading,
  handleImageDeletion,
  handleImageSelection,
  selectedIcon,
  isDialogOpen,
  setIsDialogOpen,
  isSelected,
  isDualCanvas,
  isIdentical,
  setNewImages,
  onRecalibrate,
  position,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'gallery' })
  const theme = useTheme()
  const dispatch = useDispatch()
  const newImages = useAppSelector((state) => state.app.newImages || [])
  const caseId = Cookies.get('case_id') || ''

  const [isDehazeDialog, setIsDehazeDialog] = useState(false)
  const [recipeWarningDialog, setRecipeWarningDialog] = useState<{
    type: string
    title: string
  } | null>(null)

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)

  const recipes = {
    crown: useAppSelector((state) => state.stainingStudio.crownRecipe),
    stumpShade: useAppSelector(
      (state) => state.stainingStudio.stumpShadeRecipe,
    ),
    flStumpShade: useAppSelector(
      (state) => state.fullLayering.stumpShadeRecipe,
    ),
    mlStumpShade: useAppSelector(
      (state) => state.microLayering.stumpShadeRecipe,
    ),
    fmStumpShade: useAppSelector(
      (state) => state.fullMonolithic.stumpShadeRecipe,
    ),
    flStain: useAppSelector(
      (state) => state.fullLayering.stainingCorrectionRecipe,
    ),
    mlStain: useAppSelector((state) => state.microLayering.stainingRecipe),
    fmStain: useAppSelector((state) => state.fullMonolithic.stainingRecipe),
    pfmCrown: useAppSelector(
      (state) => state.pfmCrown.stainingCorrectionRecipe,
    ),
    washBake: useAppSelector((state) => state.stainingStudio.washBake),
    flWashBake: useAppSelector((state) => state.fullLayering.washBake),
    opaquerStaining: useAppSelector(
      (state) => state.pfmCrown.opaquerStainingOutput,
    ),
  }
  const recipeCheck = {
    crown: useRecipeCheck('/staining-studio/crown', recipes.crown),
    stumpShade: useRecipeCheck(
      '/staining-studio/stump-shade',
      recipes.stumpShade,
    ),
    flStumpShade: useRecipeCheck(
      '/full-layering/stump-shade-staining',
      recipes.flStumpShade,
    ),
    mlStumpShade: useRecipeCheck(
      '/micro-layering/stump-shade-staining',
      recipes.mlStumpShade,
    ),
    fmStumpShade: useRecipeCheck(
      '/full-monolithic/stump-shade-staining',
      recipes.fmStumpShade,
    ),
    flStain: useRecipeCheck(
      '/full-layering/staining-corrections',
      recipes.flStain,
    ),
    mlStain: useRecipeCheck('/micro-layering/staining', recipes.mlStain),
    fmStain: useRecipeCheck('/full-monolithic/staining', recipes.fmStain),
    pfmCrown: useRecipeCheck('/full-monolithic/staining', recipes.pfmCrown),
    washBake: useRecipeCheck('/staining-studio/wash-bake', recipes.washBake),
    flWashBake: useRecipeCheck('/full-layering/wash-bake', recipes.flWashBake),
    opaquerStaining: useRecipeCheck(
      '/pfm-crown/opaquer-staining',
      recipes.opaquerStaining,
    ),
  }

  const isCalibrated = !!image?.calibrated_image
  const [updateRecipeImages] = useRecipeImagesUpdateMutation()

  const handleImageReset = () => {
    if (!image?.id) return

    updateRecipeImages({ id: image.id, dehazingResetedValue: true })
      .then((res) => {
        const updatedImage = res.data as CanvasImage
        const updatedDual = { ...dualCanvasImage }

        if (updatedDual[PLACEMENT.LEFT]?.id === image.id)
          updatedDual[PLACEMENT.LEFT] = updatedImage

        if (updatedDual[PLACEMENT.RIGHT]?.id === image.id)
          updatedDual[PLACEMENT.RIGHT] = updatedImage

        dispatch(setDualCanvasImage(updatedDual))
        const updatedImages = newImages.map((p, i) =>
          i === index ? updatedImage : p,
        )

        dispatch(setNewImages(updatedImages))
        dispatch(
          apiService.util.updateQueryData(
            'getCaseDetails',
            { caseId },
            (draft: any) => {
              draft.images = draft.images.map((img: any) =>
                img.id === updatedImage.id ? updatedImage : img,
              )
            },
          ) as unknown as UnknownAction,
        )
      })
      .finally(() => setIsDehazeDialog(false))
  }

  const handleImageClick = () => {
    const matched = Object.entries(recipeCheck).find(([, value]) => value)

    const isDualCanvasEmpty =
      !dualCanvasImage ||
      (isEmpty(dualCanvasImage[PLACEMENT.LEFT]) &&
        isEmpty(dualCanvasImage[PLACEMENT.RIGHT]))

    if (matched) {
      const [type] = matched

      if (isDualCanvasEmpty) {
        const actionMap: Record<string, () => void> = {
          crown: () => dispatch(clearStainingStudioCrownState()),
          stumpShade: () => dispatch(clearStumpShadeState()),
          flStumpShade: () => dispatch(clearStumpShadeStainingState()),
          mlStumpShade: () =>
            dispatch(clearMicroLayeringStumpShadeStainingState()),
          fmStumpShade: () =>
            dispatch(clearFullmonolithicStumpShadeStainingState()),
          flStain: () => dispatch(clearStainingCorrectionState()),
          mlStain: () => dispatch(clearMLStainingState()),
          fmStain: () => dispatch(clearFMStainingState()),
          pfmCrown: () => dispatch(clearPfmCrownStainingCorrectionState()),
          washBake: () => dispatch(clearWashBakeState()),
          flWashBake: () => dispatch(clearFLWashBakeState()),
          opaquerStaining: () => dispatch(clearOpaquerStainingState()),
        }

        actionMap[type]?.()
        handleImageSelection()
        return
      }

      const titles: Record<string, string> = {
        crown: 'Crown Recipe',
        stumpShade: 'Stump Shade Recipe',
        flStumpShade: 'Stump Shade Staining Recipe',
        mlStumpShade: 'Stump Shade Staining Recipe',
        fmStumpShade: 'Stump Shade Staining Recipe',
        flStain: 'Staining Studio Recipe',
        mlStain: 'Staining Studio Recipe',
        fmStain: 'Staining Studio Recipe',
        pfmCrown: 'Staining Studio Recipe',
        washBake: 'Wash Bake',
        flWashBake: 'Wash Bake',
        opaquerStaining: 'Opaquer Staining',
      }
      setRecipeWarningDialog({ type, title: titles[type] })
    } else {
      handleImageSelection()
    }
  }

  const handleRecipeReset = () => {
    const actionMap: Record<string, () => void> = {
      crown: () => dispatch(clearStainingStudioCrownState()),
      stumpShade: () => dispatch(clearStumpShadeState()),
      flStumpShade: () => dispatch(clearStumpShadeStainingState()),
      mlStumpShade: () => dispatch(clearMicroLayeringStumpShadeStainingState()),
      fmStumpShade: () =>
        dispatch(clearFullmonolithicStumpShadeStainingState()),
      flStain: () => dispatch(clearStainingCorrectionState()),
      mlStain: () => dispatch(clearMLStainingState()),
      fmStain: () => dispatch(clearFMStainingState()),
      pfmCrown: () => dispatch(clearPfmCrownStainingCorrectionState()),
      washBake: () => dispatch(clearWashBakeState()),
      flWashBake: () => dispatch(clearFLWashBakeState()),
      opaquerStaining: () => dispatch(clearOpaquerStainingState()),
    }

    if (recipeWarningDialog?.type) {
      actionMap[recipeWarningDialog.type]?.()
    }

    handleImageSelection()
    setRecipeWarningDialog(null)
  }

  const isVerticallyAligned = useAppSelector(
    (state) => state.digitalTryIn.isVerticallyAligned,
  )

  return (
    <Box
      position="relative"
      sx={{ '&:hover .action-icon': { display: 'inline-flex' } }}
    >
      <Box onClick={handleImageClick} sx={{ width: '100%', cursor: 'pointer' }}>
        <img
          key={index}
          src={
            getDehazedImageUrl(image) ||
            image?.latest_image ||
            image?.calibrated_image ||
            image?.image
          }
          alt="Recipe"
          width="100%"
          style={{
            border: `2px solid ${
              isSelected
                ? 'var(--color-accent-neongreen)'
                : isCalibrated
                  ? 'var(--color-accent-neonblue)'
                  : 'transparent'
            }`,
          }}
        />
      </Box>

      <ReactLoader
        loaded={!!isLoading}
        lines={20}
        length={20}
        width={3}
        radius={25}
        color={theme.palette.common.white}
      />

      {isSelected && selectedIcon}

      {isCalibrated && (
        <Box
          borderRadius="6.25rem"
          bgcolor="var(--color-accent-neonblue)"
          height="1.5rem"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={1.5}
          position="absolute"
          top="0.5rem"
          color={theme.palette.common.black}
          fontSize="0.8125rem"
          right={
            isSelected
              ? isDualCanvas
                ? !isIdentical
                  ? position === PLACEMENT.RIGHT && isVerticallyAligned
                    ? '5rem'
                    : '4.25rem'
                  : '2.5rem'
                : '2.5rem'
              : '0.5rem'
          }
        >
          Calibrated
          <CachedIcon
            onClick={(e) => {
              e.stopPropagation()
              onRecalibrate()
            }}
            sx={{
              marginLeft: '6px',
              color: '#004345',
              height: '16px',
              width: '16px',
              cursor: 'pointer',
            }}
          />
        </Box>
      )}

      {/* Delete Dialog */}
      <Dialog
        triggerElement={
          <Box
            className="action-icon"
            sx={{
              position: 'absolute',
              top: '0.5rem',
              left: '0.5rem',
              display: 'none',
              cursor: 'pointer',
              background: theme.palette.background.errorContainer,
              p: 1.125,
              borderRadius: 100,
              transition:
                'box-shadow 0.2s ease-in-out, opacity 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                opacity: 0.8,
              },
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsDialogOpen(true)
            }}
          >
            <Icon
              icon={FaTrashAlt}
              size={18}
              color={theme.palette.error.main}
            />
          </Box>
        }
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={t('imageRemoveDialog.title')}
        content={
          <Box>
            <Box pb={3}>{t('imageRemoveDialog.description1')}</Box>
            <Box>{t('imageRemoveDialog.description2')}</Box>
          </Box>
        }
        paperStyles={{ width: '600px' }}
        actionButtons={
          <>
            <Button
              variant="outlined"
              label={t('imageRemoveDialog.cancelButton')}
              onClick={() => setIsDialogOpen(false)}
              textTransform="uppercase"
            />
            <Button
              label={t('imageRemoveDialog.removeButton')}
              onClick={handleImageDeletion}
              textTransform="uppercase"
            />
          </>
        }
      />

      {/* Dehaze Dialog */}
      {isDehazeIconVisible(image) && (
        <Dialog
          triggerElement={
            <Box
              className="action-icon"
              sx={{
                position: 'absolute',
                top: '0.5rem',
                left: '3.2rem',
                display: 'none',
                cursor: 'pointer',
                background: theme.palette.warning.main,
                p: 1.125,
                borderRadius: 100,
                transition:
                  'box-shadow 0.2s ease-in-out, opacity 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  opacity: 0.8,
                },
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsDehazeDialog(true)
              }}
            >
              <Icon
                icon={LuRefreshCw}
                size={18}
                color={theme.palette.common.black}
              />
            </Box>
          }
          isOpen={isDehazeDialog}
          onClose={() => setIsDehazeDialog(false)}
          title={t('resetImageDialog.title')}
          content={<Box>{t('resetImageDialog.description')}</Box>}
          paperStyles={{ width: '600px' }}
          actionButtons={
            <>
              <Button
                variant="outlined"
                label={t('resetImageDialog.cancelButton')}
                onClick={() => setIsDehazeDialog(false)}
                textTransform="uppercase"
              />
              <Button
                label={t('resetImageDialog.resetButton')}
                onClick={handleImageReset}
                textTransform="uppercase"
              />
            </>
          }
        />
      )}

      {/* Recipe Warning Dialog */}
      <Dialog
        triggerElement={null}
        isOpen={recipeWarningDialog !== null}
        onClose={() => setRecipeWarningDialog(null)}
        title={recipeWarningDialog?.title || ''}
        content={<Box>{t('selectDifferentImageDialog.description')}</Box>}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <>
            <Button
              variant="outlined"
              label={t('selectDifferentImageDialog.cancelButton')}
              onClick={() => setRecipeWarningDialog(null)}
            />
            <Button
              variant="contained"
              label={t('selectDifferentImageDialog.okButton')}
              onClick={handleRecipeReset}
            />
          </>
        }
      />
    </Box>
  )
}

export default GalleryImage
